import type { ClientCompanyCode } from '../models/appState';

/**
 * Resolve the deploy's company code (the `<company>` in the `name = <company>/<type>`
 * order_template convention used to address DB-served templates by name).
 *
 * Single source of the `'sbi'` fallback so it cannot diverge across the call sites
 * that each used to inline `process.env.VITE_COMPANY_CODE || 'sbi'` (App shell, the
 * questionnaire form-config loader, the print route, and the editor hooks).
 *
 * Prefer an explicit ambient AppState companyCode when the caller has one, fall back
 * to the build-time VITE_COMPANY_CODE, then 'sbi'.
 */
export const resolveCompanyCode = (appStateCompanyCode?: string): string =>
  appStateCompanyCode || process.env.VITE_COMPANY_CODE || 'sbi';

const isClientCompanyCode = (code: string): code is ClientCompanyCode =>
  code === 'sk' || code === 'sbi';

/**
 * Same resolution as resolveCompanyCode, narrowed to the ClientCompanyCode union
 * for call sites that require it (e.g. the headless print AppStateProvider). Any
 * unknown/legacy value normalizes to the 'sbi' default — the only two tenants are
 * sk/sbi — so the consumer never needs an unchecked cast.
 */
export const resolveClientCompanyCode = (
  appStateCompanyCode?: string,
): ClientCompanyCode => {
  const code = resolveCompanyCode(appStateCompanyCode);
  return isClientCompanyCode(code) ? code : 'sbi';
};
